import React from 'react';
import { connect } from 'react-redux';
import { AssetDownscaleStatuses } from '../../../../../common/src/domain/asset/assetDownscaleStatuses';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { IValidationState } from '../../../mixins/validation';
import * as companyActions from '../../../store/company/actions';
import { fetchDelete, fetchGet, fetchPost } from '../../../store/fetch';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { formatBytes } from '../../../utils/formatBytes';
import { formatTimestamp } from '../../../utils/formatTimestamp';
import { IConnectProps } from '../../../utils/IConnectProps';
import { isCustomerVersion } from '../../../utils/isCustomerVersion';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    Panel,
    PanelBody,
    PanelHorizontalSpacing,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { DisplayMessage } from '../../utils/displayMessage';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { AssetDropzone } from '../Common/AssetDropzone';
import SurveyVideoDetails from '../Common/SurveyVideoDetails';
import { IAsset } from '../Interfaces/IAsset';
import { IElement } from '../Interfaces/IElement';

interface ISurveyEditVideosProps extends ITranslateProps, IConnectProps {
    userToken: string;
    objectId: string;
    surveyId: string;
}

interface ISurveyEditVideosState extends IValidationState {
    objectName?: undefined | string;
    objectCompositionIsVisible?: undefined | boolean;
    isAssetDescriptionVisible?: undefined | boolean;
    type: string;
    elements: IElement[];
    assets: IAsset[];
    isUploading?: undefined | boolean;
    openVideoDetails?: undefined | string;
    assetIdEditName?: undefined | string;
    inspectionStandardType?: undefined | string;
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userToken: authentication.token,
    };
}

class SurveyEditVideos extends ComponentSetStatePromised<ISurveyEditVideosProps, ISurveyEditVideosState> {
    private mounted: boolean;
    public interval!: NodeJS.Timeout;

    constructor(props: ISurveyEditVideosProps) {
        super(props);
        this.mounted = false;

        this.state = {
            submitting: false,
            failed: false,
            saved: false,
            type: assetTypes.SurveyVideo,
            elements: [],
            assets: [],
        };
    }

    public override async componentDidMount() {
        this.mounted = true;
        await this.load();
        this.interval = setInterval(() => {
            void this.loadAssets();
        }, 20000);
    }

    public override componentWillUnmount() {
        this.mounted = false;
        clearInterval(this.interval);
    }

    public override render() {
        return (
            <Tray>
                {this.renderMessage()}
                <Panel>
                    <PanelBody>
                        <PanelHorizontalSpacing>
                            <AssetDropzone
                                onComplete={this.onDropzoneComplete.bind(this)}
                                onError={this.onDropzoneError.bind(this)}
                                acceptedFiles=".mp4,.mov,.mts,.avi"
                                objectId={this.props.objectId}
                                surveyId={this.props.surveyId}
                                assetType={this.state.type}
                                onSending={() => this.setState({ isUploading: true })}
                                onQueueComplete={() => this.setState({ isUploading: false })}
                                autoProcessQueue={true}
                            />
                        </PanelHorizontalSpacing>
                        <Table spaced>
                            <TableHead>
                                <TableHeadRow>
                                    <TableHeader>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                void this.loadAssets();
                                            }}
                                            className="btn btn-default btn-xs"
                                            title="Refresh"
                                        >
                                            <i className="fa fa-refresh" />
                                        </button>
                                    </TableHeader>
                                    <TableHeader>{this.props.t('common.dateAdded')}</TableHeader>
                                    <TableHeader>{this.props.t('common.size')}</TableHeader>
                                    <TableHeader>{this.props.t('common.duration')}</TableHeader>
                                    <TableHeader>{this.props.t('common.filename')}</TableHeader>
                                    <TableHeader>&nbsp; </TableHeader>
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {this.state.assets.map((asset, i) => (
                                    <React.Fragment key={i}>
                                        <TableRow>
                                            <TableData>
                                                <button
                                                    onClick={async () => this.openVideoDetails(asset.id)}
                                                    type="button"
                                                    className="btn btn-primary btn-sm fs12"
                                                >
                                                    <span className={this.state.openVideoDetails === asset.id ? 'fa fa-minus' : 'fa fa-plus'} />
                                                </button>
                                            </TableData>
                                            <TableData>
                                                <NullableFormattedDate value={asset.date} />
                                            </TableData>

                                            {asset.downscaleStatusHigh === AssetDownscaleStatuses.Complete ||
                                            (asset.downscaleStatusLow && asset.downscaleStatusLow === AssetDownscaleStatuses.Complete) ? (
                                                <>
                                                    <TableData>{formatBytes(asset.size)}</TableData>
                                                    <TableData>{asset.duration ? formatTimestamp(asset.duration) : '-'}</TableData>
                                                </>
                                            ) : (
                                                <>
                                                    <TableData>-</TableData>
                                                    <TableData>-</TableData>
                                                </>
                                            )}
                                            <TableData>
                                                {this.renderAssetName(
                                                    asset.name,
                                                    asset.id,
                                                    asset.downscaleStatusHigh === AssetDownscaleStatuses.Complete,
                                                )}
                                            </TableData>
                                            <td style={{ whiteSpace: 'nowrap', width: '80px' }}>
                                                <IconEditButton onClick={() => this.setState({ assetIdEditName: asset.id })} />
                                                <IconRemoveButton
                                                    removeQuestion={this.props.t('common.confirmDelete', asset.name)}
                                                    onClick={async () => this.remove(asset.id)}
                                                />
                                            </td>
                                        </TableRow>
                                        <tr>
                                            {this.state.openVideoDetails === asset.id && (
                                                <td colSpan={5}>
                                                    <SurveyVideoDetails
                                                        elements={this.state.elements}
                                                        surveyId={this.props.surveyId}
                                                        inspectionStandardType={this.state.inspectionStandardType!}
                                                        objectId={this.props.objectId}
                                                        isAssetDescriptionVisible={this.state.isAssetDescriptionVisible}
                                                        {...asset}
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                        {isCustomerVersion('ronik') && (
                            <div className="fs12 fw200">
                                <p>
                                    To downsize videos before uploading you can use ffmpeg (https://www.ffmpeg.org/). This will result in a smaller
                                    file size without a noticeable loss of quality. <br />
                                    The commando is:
                                </p>
                                <p>./ffmpeg -i [FILENAME.MOV] -crf 23 -vf scale=1920:1080 -preset medium -movflags +faststart [FILENAME.mp4]</p>
                            </div>
                        )}
                    </PanelBody>
                </Panel>
            </Tray>
        );
    }

    private onDropzoneError(errorCode: string) {
        this.setState({ errorCode, failed: true });
    }

    private onDropzoneComplete() {
        void this.loadAssets();
        this.props.dispatch(companyActions.getCompany());
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}`;
        const result = await fetchGet(url);
        await this.setStatePromised(result.json);

        await this.loadObject(this.props.objectId);
        await this.loadElements(this.props.objectId);
        await this.loadAssets();
    }

    private async loadAssets() {
        if (this.mounted) {
            const url = `/api/assets?surveyId=${this.props.surveyId}&types=${assetTypes.SurveyVideo}`;
            const result = await fetchGet(url);
            await this.setStatePromised({ assets: result.json });
        }
    }

    private async openVideoDetails(assetId: string) {
        if (this.state.openVideoDetails === assetId) {
            await this.setStatePromised({ openVideoDetails: undefined });
        } else {
            await this.setStatePromised({ openVideoDetails: assetId });
        }
    }

    private async loadObject(objectId: string) {
        const url = `/api/objects/${objectId}?fields=name,compositionIsVisible`;
        const result = await fetchGet(url);
        await this.setStatePromised({ objectName: result.json.name, objectCompositionIsVisible: result.json.compositionIsVisible });
    }

    private renderMessage(): JSX.Element | null {
        if (this.state && this.state.message) {
            return <DisplayMessage message={this.state.message} messageType="general" />;
        } else if (this.state.failed) {
            let message: string;

            switch (this.state.errorCode) {
                case 'STORAGE_LIMIT_REACHED':
                    message = this.props.t('asset.errorStorageLimitReached');
                    break;
                case 'FILE_TOO_BIG':
                    message = this.props.t('asset.errorFileTooBig');
                    break;
                default:
                    message = `${this.props.t('validation.general')} : ${this.state.errorCode}`;
            }

            return <DisplayMessage message={message} messageType="error" />;
        } else if (this.state.saved) {
            return <DisplayMessage message={this.props.t('validation.general')} messageType="saved" />;
        }
        return null;
    }

    private renderAssetName(assetName: string, assetId: string, isDownscalingHighFinished: boolean): JSX.Element {
        if (this.state.assetIdEditName === assetId) {
            return (
                <input
                    type="text"
                    name="titleClip"
                    id="titleClip"
                    className="gui-input fs13"
                    placeholder={this.props.t('video.titleInput')}
                    defaultValue={assetName}
                    onBlur={async (e) => this.saveAssetName(e, assetId)}
                />
            );
        } else if (isDownscalingHighFinished) {
            return <a href={`/api/assets/${assetId}/download?downscaledHigh=true`}>{assetName}</a>;
        }
        return <span>{assetName}</span>;
    }

    private async remove(id: string) {
        const url = `/api/surveys/${this.props.surveyId}/assets/${id}`;
        await fetchDelete(url);
        await this.loadAssets();
        this.props.dispatch(companyActions.getCompany());
    }

    private async saveAssetName(e: any, assetId: string) {
        e.preventDefault();

        const data = { name: e.target.value };

        const url = `/api/surveys/${this.props.surveyId}/assets/${assetId}`;
        // const url = `/api/assets/${assetId}`;
        await fetchPost(url, {}, data);
        await this.setStatePromised({ assetIdEditName: undefined });
        await this.loadAssets();
    }

    private async loadElements(objectId: string) {
        const url = `/api/objects/${objectId}/elements?surveyId=${this.props.surveyId}`;
        const result = await fetchGet(url);
        await this.setStatePromised({ elements: result.json });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(SurveyEditVideos));
